import { Component, Vue } from 'vue-property-decorator';
import { ContainerForm, Hint, Link } from '@/components';
import { CreateElement, VNode } from 'vue';
import { formMixins } from '@/components/SignUpForm/mixins';
import { mapActions, mapGetters } from 'vuex';
import { Rules, VuetifyInput } from '@/types';
import { VCheckbox, VTextField } from 'vuetify/lib';
import { ISignUpForm, ISignUpComponent, IRegistrationTranslation } from '@/types';
import { applyInterpolation } from '@/utils';

@Component({
  name: 'SignUpForm',
  computed: {
    ...mapGetters('accounts', ['translations']),
    ...mapGetters({
      messages: 'translations',
    }),
    errorsMessages() {
      return (this as any).messages?.field_errors;
    },
    t(): ISignUpForm { return (this.translations as IRegistrationTranslation).signUp; },
  },
  methods: {
    ...mapActions('accounts', ['createNewUser', 'setUserPhoneEntered', 'set2faCode']),
  },
})
export default class SignUpForm extends Vue implements ISignUpComponent {
  public errorBucket: Map<string, []> = new Map();
  public hasErrors: boolean = true;
  public formRules: Rules = formMixins.rules;
  public setUserPhoneEntered;
  public t!: ISignUpForm;

  private instanceEmail: string = '';
  private firstName: string = '';
  private isAgree: boolean = true;
  private lastName: string = '';
  private password1: string = '';
  private password2: string = '';
  private phone: string = '+';
  private showPassword1: boolean = false;
  private showPassword2: boolean = false;

  get checkFieldsIsValid() {
    const bucket: boolean[] = [];
    let notValid = true;
    Object.keys(this.form).forEach((f) => {
      if ((this.$refs[f] as VuetifyInput)) {
        bucket.push(!(this.$refs[f] as VuetifyInput).lazyValue
          || (this.$refs[f] as VuetifyInput).error
          || !(this.$refs[f] as VuetifyInput).valid);
        notValid = bucket.some((x) => x);
        return notValid;
      }
    });
    return notValid;
  }

  get form() {
    return {
      first_name: this.firstName,
      last_name: this.lastName,
      email: this.instanceEmail,
      phone: this.phone,
      password1: this.password1,
      password2: this.password2,
      is_agree_with_agreement: this.isAgree,
    };
  }

  private passwordMatches() {
    return (this.password1 === this.password2)
      || (this.t?.errorsMessages?.passwordNotMatch) || '';
  }

  private toggleAnimation() {
    const list = document.getElementsByTagName('label');
    // tslint:disable-next-line: prefer-const
    for (let item of list) {
      if (item.style.animation !== 'none') {
        item.style.animation = 'shake 0.6s cubic-bezier(0.25, 0.8, 0.5, 1)';
      } else {
        item.style.animation = 'none';
      }
    }
  }

  private updateErrorBucket(key: string) {
    if (this.errorBucket.has(key)) {
      this.errorBucket.delete(key);
    }
  }

  private render(createElement: CreateElement): VNode {
    const name: VNode = createElement('div', {
      style: {
        'margin-top': this.$vuetify.breakpoint.xsOnly ? '10px' : '40px',
      },
      class: {
        'sing-up-form__input-wrapper': true,
        'input-wrapper': true,
        flex: true,
        'flex-column': this.$vuetify.breakpoint.xsOnly,
      },
    }, [
      createElement(VTextField, {
        attrs: {
          name: 'firstName',
        },
        props: {
          id: 'firstName',
          'browser-autocomplete': 'given-name',
          height: 28,
          label: this.t.firstName,
          error: this.errorBucket.has('firstName'),
          errorMessages: this.errorBucket.get('firstName'),
          rules: [
            this.formRules.required(this.firstName, (this as any).errorsMessages?.required),
            this.formRules.namesOnlyLetters(this.firstName, (this as any).errorsMessages?.only_letters),
            this.formRules.nameMin(
              this.firstName,
              applyInterpolation(
                (this as any).errorsMessages?.min_length, {
                value: '',
                min: 2,
              },
              ),
              2,
            ),
            this.formRules.nameMax(
              this.firstName,
              applyInterpolation(
                (this as any).errorsMessages?.max_length, {
                value: '',
                max: 30,
              },
              ),
              30,
            ),
          ],
          validateOnBlur: true,
        },
        class: {
          'sing-up-form__input': true,
        },
        on: {
          input: (value: string) => {
            this.firstName = value;
            this.updateErrorBucket('firstName');
          },
        },
        ref: 'first_name',
      }),
      createElement(VTextField, {
        attrs: {
          name: 'lastName',
        },
        props: {
          validateOnBlur: true,
          id: 'lastName',
          'browser-autocomplete': 'family-name',
          height: 28,
          label: this.t.lastName,
          error: this.errorBucket.has('lastName'),
          errorMessages: this.errorBucket.get('lastName'),
          rules: [
            this.formRules.required(this.lastName, (this as any).errorsMessages?.required),
            this.formRules.namesOnlyLetters(this.lastName, (this as any).errorsMessages?.only_letters),
            this.formRules.nameMin(
              this.lastName,
              applyInterpolation(
                (this as any).errorsMessages?.min_length, {
                value: '',
                min: 2,
              },
              ),
              2,
            ),
            this.formRules.nameMax(
              this.lastName,
              applyInterpolation(
                (this as any).errorsMessages?.max_length, {
                value: '',
                max: 150,
              },
              ),
              150,
            ),
          ],
        },
        on: {
          input: (value: string) => {
            this.lastName = value;
            this.updateErrorBucket('lastName');
          },
        },
        ref: 'last_name',
      }),
    ]);

    const phone: VNode = createElement(VTextField, {
      attrs: {
        name: 'phone',
      },
      directives: [
        {
          name: 'mask',
          value: '+## ### ### ## ##',
        },
      ],
      props: {
        id: 'phone',
        'browser-autocomplete': 'tel',
        value: '+',
        height: 28,
        label: this.t.phone,
        error: this.errorBucket.has('phone'),
        errorMessages: this.errorBucket.get('phone'),
        rules: [
          this.formRules.required(this.phone, (this as any).errorsMessages?.required),
          this.formRules.phone(this.phone, (this as any).errorsMessages?.phone),
        ],
        validateOnBlur: true,
      },
      class: {
        'sing-up-form__input': true,
        'input-wrapper': true,
      },
      on: {
        input: (value: string) => {
          this.phone = value.replace(/ /g, '');
          this.updateErrorBucket('phone');
        },
      },
      ref: 'phone',
    });

    const email: VNode = createElement(VTextField, {
      attrs: {
        name: 'email',
      },
      props: {
        id: 'email',
        'browser-autocomplete': 'email',
        height: 28,
        label: this.t.email,
        error: this.errorBucket.has('email'),
        errorMessages: this.errorBucket.get('email'),
        rules: [
          this.formRules.required(this.instanceEmail, (this as any).errorsMessages?.required),
          this.formRules.email(this.instanceEmail, {
            at: (this as any).errorsMessages?.email,
            user: (this as any).errorsMessages?.email,
            domain: (this as any).errorsMessages?.email,
          }),
        ],
        validateOnBlur: true,
      },
      on: {
        input: (value: string) => {
          this.instanceEmail = value;
          this.updateErrorBucket('email');
        },
      },
      class: {
        'sing-up-form__input': true,
        'input-wrapper': true,
      },
      ref: 'email',
    });

    const password1: VNode = createElement(VTextField, {
      attrs: {
        name: 'password',
      },
      props: {
        id: 'password1',
        validateOnBlur: true,
        'append-icon': this.showPassword1 ? '$vuetify.icons.visible' : '$vuetify.icons.visible_off',
        type: this.showPassword1 ? 'text' : 'password',
        height: 28,
        error: this.errorBucket.has('password1'),
        errorMessages: this.errorBucket.get('password1'),
        label: this.t.password1,
        rules: [
          this.formRules.required(this.password1, (this as any).errorsMessages?.required),
          this.formRules.nameMin(
            this.password1,
            applyInterpolation(
              (this as any).errorsMessages?.min_length, {
              value: '',
              min: 8,
            },
            ),
            8,
          ),
          this.formRules.nameMax(
            this.password1,
            applyInterpolation(
              (this as any).errorsMessages?.max_length, {
              value: '',
              max: 254,
            },
            ),
            254,
          ),
          this.formRules.withoutCyrillic(this.password1, this.t?.errorsMessages?.passwordContent),
          this.formRules.password(this.password1, this.t?.errorsMessages?.passwordTooSimple),
        ],
      },
      class: {
        'sing-up-form__input': true,
        'input-wrapper': true,
      },
      on: {
        input: (value: string) => {
          this.password1 = value;
          this.updateErrorBucket('password1');
        },
        'click:append': () => this.showPassword1 = !this.showPassword1,
      },
      ref: 'password1',
    });

    const password2: VNode = createElement(VTextField, {
      attrs: {
        name: 'confirmPassword',
      },
      props: {
        id: 'password2',
        validateOnBlur: true,
        'append-icon': this.showPassword2 ? '$vuetify.icons.visible' : '$vuetify.icons.visible_off',
        type: this.showPassword2 ? 'text' : 'password',
        height: 28,
        label: this.t.password2,
        error: this.errorBucket.has('password2'),
        errorMessages: this.errorBucket.get('password2'),
        rules: [
          this.formRules.required(this.password2, (this as any).errorsMessages?.required),
          this.formRules.nameMin(
            this.password2,
            applyInterpolation(
              (this as any).errorsMessages?.min_length, {
              value: '',
              min: 8,
            },
            ),
            8,
          ),
          this.formRules.nameMax(
            this.password2,
            applyInterpolation(
              (this as any).errorsMessages?.max_length, {
              value: '',
              max: 254,
            },
            ),
            254,
          ),
          this.formRules.withoutCyrillic(this.password2, (this as any).errorsMessages?.passwordContent),
          this.formRules.password(this.password2, this.t?.errorsMessages?.passwordTooSimple),
          this.passwordMatches(),
        ],
      },
      class: {
        'sing-up-form__input': true,
        'input-wrapper': true,
      },
      on: {
        input: (value: string) => {
          this.password2 = value;
        },
        'click:append': () => this.showPassword2 = !this.showPassword2,
      },
      ref: 'password2',
    });

    const agreement: VNode = createElement(VCheckbox, {
      attrs: {
        name: 'checkbox',
      },
      props: {
        id: 'agreement',
        rules: [this.formRules.canBeAccess(this.isAgree, (this as any).errorsMessages?.conditions)],
        'input-value': this.isAgree,
        color: '#EC3D3D',
        'off-icon': '$vuetify.icons.checkboxOff_Error',
      },
      class: {
        'sing-up-form__input': true,
        'mt-0': true,
      },
      on: {
        change: () => {
          this.isAgree = !this.isAgree;
        },
        '&click': (e) => {
          if (e.target.nodeName === 'A') {
            const evt = new MouseEvent('click', {
              bubbles: false,
              cancelable: false,
              view: window,
            });
            e.target.dispatchEvent(evt);
            this.isAgree = !this.isAgree;
          }
        },
      },
      ref: 'is_agree_with_agreement',
    }, [
      createElement('template', {
        slot: 'label',
      }, [
        createElement(Link, {
          class: {
            'sing-up-form-input__agreement-label': true,
          },
          props: {
            html: this.t.agreement,
          },
          attrs: {
            absolute: true,
          },
        }),
      ]),
    ]);

    const actionButton: VNode = createElement('button', {
      domProps: {
        disabled: this.checkFieldsIsValid,
      },
      style: [
        {
          'margin-top': this.$vuetify.breakpoint.xsOnly ? '30px' : '40px',
        },
      ],
      class: [
        this.$vuetify.breakpoint.xsOnly
          ? 'orange-big-button__mobile'
          : 'orange-big-button',
      ],
      attrs: {
        type: 'submit',
      },
      on: {
        click: async (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleAnimation();
          this.createNewUser(this.form).then((response) => {
            if (response && (response.status === 400 || response.status === 401) && response.data) {
              this.errorBucket = new Map();
              Object.keys(response.data).forEach((key) => {
                if (key !== 'status_code' && key !== 'mobile_errors') {
                  this.errorBucket.set(key, response.data[key]);
                }
              });
            } else if (response && response.status === 201) {
              this.$gtm.trackEvent({
                eventCategory: 'registration_rent',
                eventAction: 'click',
                eventLabel: 'registration_success',
                event: 'registration_success',
              });
              this.setUserPhoneEntered(response.data.phone);
              this.set2faCode(response.data.id);
              this.$router.push({
                name: 'confirm',
                params: {
                  userEnterPhone: response.data.masked_phone,
                },
              });
            }
          }).catch((error) => console.error(error));
        },
      },
    }, this.t.buttonName);

    const hint: VNode = createElement(Hint, {
      props: {
        text: this.t.hint,
        linkTo: 'accounts',
      },
      style: [
        {
          'align-self': 'center',
          'margin-top': '19px',
        },
        this.$vuetify.breakpoint.xsOnly && { 'margin-bottom': '40px' },
      ],
      slot: 'bottom',
    });

    const form = createElement(ContainerForm, {
      ref: 'container',
    }, [
      this.$slots.title,
      name,
      phone,
      email,
      password1,
      password2,
      agreement,
      actionButton,
      hint,
    ]);
    return form;
  }
}
